import { QueryClient } from '@tanstack/react-query';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

let initializationError = null;

const emitSecurityError = (error, context) => {
    initializationError = {
        errorName: error?.name || 'Error',
        errorMessage: error?.message || String(error),
        context,
        timestamp: Date.now(),
    };

    console.error(`🚨 [Keycloak] Initialisation impossible (${context}):`, initializationError.errorMessage);
    window.dispatchEvent(new CustomEvent('keycloakSecurityError', { detail: initializationError }));
};

export const getAuthenticationInitializationError = () => initializationError;

const createUnavailableUserManager = (config, error) => {
    const unavailable = async () => { throw error; };
    const eventTypes = [
        'UserLoaded',
        'UserUnloaded',
        'AccessTokenExpiring',
        'AccessTokenExpired',
        'SilentRenewError',
        'UserSignedOut',
    ];
    const events = Object.fromEntries(eventTypes.flatMap((eventType) => [
        [`add${eventType}`, () => {}],
        [`remove${eventType}`, () => {}],
    ]));

    return {
        settings: config,
        events,
        signinRedirect: unavailable,
        signinCallback: unavailable,
        signoutRedirect: unavailable,
        getUser: unavailable,
        removeUser: async () => {},
        signinSilent: unavailable,
    };
};

const createUserManager = () => {
    const config = {
        authority: window.ENV.REACT_APP_KEYCLOAK_URL,
        client_id: window.ENV.REACT_APP_KEYCLOAK_CLIENT,
        redirect_uri: `${window.location.origin}${window.location.pathname}`,
        post_logout_redirect_uri: window.location.origin,
        monitorSession: false,
        automaticSilentRenew: false,
        requestTimeoutInSeconds: 5,
        silentRequestTimeoutInSeconds: 5,
        accessTokenExpiringNotificationTimeInSeconds: 60,
    };

    let userStore;
    let stateStore;
    try {
        const storage = window.sessionStorage;
        const testKey = `__oidc_storage_test_${window.crypto?.randomUUID?.()
            || `${Date.now()}_${Math.random().toString(36).slice(2)}`}`;
        storage.setItem(testKey, '1');
        try {
            if (storage.getItem(testKey) !== '1') {
                throw new Error('sessionStorage ne conserve pas les données écrites');
            }
        } finally {
            storage.removeItem(testKey);
        }
        userStore = new WebStorageStateStore({ store: storage });
        stateStore = new WebStorageStateStore({ store: storage });
    } catch (error) {
        emitSecurityError(error, 'sessionStorage');
        return createUnavailableUserManager(config, error);
    }

    try {
        return new UserManager({ ...config, userStore, stateStore });
    } catch (error) {
        emitSecurityError(error, 'UserManager');
        return createUnavailableUserManager(config, error);
    }
};

export const userManager = createUserManager();

export const onSigninCallback = () => {
    window.history.replaceState({}, document.title, window.location.pathname);
};

export const queryClient = new QueryClient();
